import { useState, type FormEvent } from 'react';
import { get, post, put } from '../api';
import { Field, Problems } from '../components/Field';
import { toast } from '../components/toast';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import type { ProviderInfo } from './EmailSettings';

interface SmsStatus {
  provider: string;
  providers: ProviderInfo[];
  sender: string;
  options: Record<string, string>;
  has_secret: boolean;
  can_store_keys: boolean;
}

/** How this business sends SMS: its own gateway account, or by hand from a phone. */
export function SmsSettings() {
  const [tick, setTick] = useState(0);
  const status = useLoad(() => get<SmsStatus>('/api/settings/sms'), [tick]);
  const [choice, setChoice] = useState<string | null>(null);
  const [sender, setSender] = useState<string | null>(null);
  const [options, setOptions] = useState<Record<string, string> | null>(null);
  const [secret, setSecret] = useState('');
  const [testTo, setTestTo] = useState('');
  const [test, setTest] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});

  const s = status.data;
  if (status.loading && !s) return null;
  if (status.error || !s || !Array.isArray(s.providers)) return null;

  const intro = (
    <>
      <h2 id="sms-h">SMS</h2>
    </>
  );

  if (s.providers.length === 0) {
    return (
      <section className="stack" aria-labelledby="sms-h">
        {intro}
        <p className="muted">
          No SMS gateway is installed on this server yet. SMS messages can be set to Manual, which
          opens your phone&apos;s messaging app with the message filled in.
        </p>
      </section>
    );
  }

  const provider = choice ?? s.provider;
  const info = s.providers.find((p) => p.id === provider);
  const optionValues: Record<string, string> = Object.fromEntries(
    (info?.options ?? []).map((o) => [
      o.key,
      options?.[o.key] ??
        (provider === s.provider ? s.options[o.key] : undefined) ??
        o.default ??
        '',
    ]),
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    try {
      await put('/api/settings/sms', {
        provider,
        sender: sender ?? s!.sender,
        options: optionValues,
        secret,
      });
      setSecret('');
      setOptions(null);
      toast('Saved.');
      setTick((t) => t + 1);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    }
  }

  async function sendTest() {
    setTest('');
    try {
      const r = await post<{ ok: boolean; error?: string }>('/api/settings/sms/test', {
        to: testTo,
      });
      setTest(r.ok ? `A test SMS was sent to ${testTo}.` : `The test failed: ${r.error}`);
    } catch (err) {
      setTest(describe(err).problems[0] ?? 'The test failed.');
    }
  }

  return (
    <section className="stack" aria-labelledby="sms-h">
      {intro}
      <p className="muted">
        Use your own SMS gateway account to send messages automatically. Without one, SMS messages
        are prepared for you to send from your phone.
      </p>
      <Problems problems={problems} />
      <form onSubmit={save} className="stack" noValidate>
        <fieldset>
          <legend>Sending</legend>
          <Field label="Send SMS using" error={fields.provider}>
            <select
              value={provider}
              onChange={(e) => {
                setChoice(e.target.value);
                setOptions(null);
              }}
            >
              <option value="off">Do not send SMS automatically</option>
              {s.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          {info && (
            <>
              <Field label="Sender (number or name)" error={fields.sender}>
                <input value={sender ?? s.sender} onChange={(e) => setSender(e.target.value)} />
              </Field>
              {info.options.map((o) => (
                <Field key={o.key} label={o.label} error={fields[o.key]}>
                  {o.kind === 'select' ? (
                    <select
                      value={optionValues[o.key]}
                      onChange={(e) => setOptions({ ...optionValues, [o.key]: e.target.value })}
                    >
                      {(o.choices ?? []).map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={optionValues[o.key]}
                      onChange={(e) => setOptions({ ...optionValues, [o.key]: e.target.value })}
                    />
                  )}
                </Field>
              ))}
              <Field
                label={
                  s.has_secret && provider === s.provider
                    ? `${info.secret_label} (leave blank to keep)`
                    : info.secret_label
                }
                error={fields.secret}
              >
                <input
                  type="password"
                  autoComplete="off"
                  value={secret}
                  disabled={!s.can_store_keys}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </Field>
            </>
          )}
        </fieldset>
        <div className="row">
          <button type="submit">Save SMS settings</button>
        </div>
      </form>
      {s.provider !== 'off' && (
        <div className="row wrap">
          <Field label="Send a test SMS to">
            <input
              type="tel"
              inputMode="tel"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
          </Field>
          <button type="button" className="quiet" onClick={() => void sendTest()}>
            Send test SMS
          </button>
          {test && <p role="status">{test}</p>}
        </div>
      )}
    </section>
  );
}
